<template>
  <!-- Replacement Quote System Summary page: breadcrumb + RqSummaryCard (the hosted quotebuilder summary the app showed in a
       web view, in the app theme) with Edit Summary on its band; if the summary fails the page goes back to the RQ detail. -->
  <div class="w-full font-roboto">
    <div
      class="w-full relative border-1 rounded-2xl px-[10px] laptop:px-[20px] pb-[10px] laptop:pb-[25px] largeDesktop:px-[30px] largeDesktop:pb-[35px] pt-[35px] border-gray-300">
      <DetailBackButton :fallback="fromAdmin ? '/admin-utilities' : detailPath" />
      <!-- breadcrumb bar, same as the RQ Data Sheet page; no header breadcrumb on this page -->
      <div
        class="flex flex-wrap items-center gap-x-[6px] gap-y-1 min-h-[40px] mb-[15px] px-[16px] py-[8px] rounded-[8px] bg-white shadow-[0px_0px_10px_0px_#0000001A] text-[13px]">
        <template v-for="(crumb, index) in crumbs" :key="crumb.label">
          <span v-if="index > 0" class="text-[#CCCCCC]">|</span>
          <a v-if="crumb.to" :href="$router.resolve(crumb.to).href" class="no-underline text-[#8479E1]"
            @click="openCrumb($event, crumb.to)">{{ crumb.label }}</a>
          <span v-else class="text-[#000000]">{{ crumb.label }}</span>
        </template>
      </div>

      <RqSummaryCard ref="card" :order-id="orderId" @failed="onFailed">
        <template #band>
          <div class="flex flex-wrap items-center gap-[8px]">
            <!-- not during Submit PerfectMatch (?submit=1): only Add Additional Items / Finalize there -->
            <router-link v-if="canEdit && !isSubmitFlow" :to="{ path: `/replacement-quote/summary/${orderId}/edit`, query: fromAdmin ? { from: 'admin' } : {} }" active-class=""
              class="inline-flex items-center h-[34px] px-[14px] rounded-full border-1 border-white text-[13px] text-white no-underline hover:bg-[rgba(255,255,255,0.15)]">Edit Summary</router-link>
            <!-- Submit PerfectMatch (?submit=1): Additional Items / Finalize open here (RqSubmitModal) -->
            <template v-if="isSubmitFlow">
              <button type="button" @click="$refs.submitModal.open(1)"
                class="inline-flex items-center h-[34px] px-[14px] rounded-full border-1 border-white bg-transparent text-[13px] text-white hover:bg-[rgba(255,255,255,0.15)]">Add Additional Items</button>
              <button type="button" @click="$refs.submitModal.open(2)"
                class="inline-flex items-center h-[34px] px-[14px] rounded-full border-0 bg-[#fff] text-[13px] font-medium text-[#8479E1] hover:bg-[#F3F0FF]">Finalize the quote</button>
            </template>
          </div>
        </template>
      </RqSummaryCard>
      <!-- Save Items → the summary shows the new additional items; submitted → back to the RQ detail -->
      <RqSubmitModal ref="submitModal" :order-id="orderId" @saved="$refs.card.load()" @submitted="$router.push(detailPath)" />
    </div>

  </div>
</template>

<script>
import { useToast } from "vue-toastification";
import DetailBackButton from "@/components/partialComponent/DetailBackButton.vue";
import RqSummaryCard from "@/components/replacementQuote/RqSummaryCard.vue";
import RqSubmitModal from "@/components/replacementQuote/RqSubmitModal.vue";
import { rqApi } from "@/services/rqApi";
import { isSilentLogin } from "@/helpers/embedMode";
import { isNewTabClick } from "@/helpers/newTabClick";
import { goBackTo } from "@/helpers/historyPositions";
import { mapGetters } from "vuex";
import { menuHasRoute } from "@/helpers/techMenu";
const toast = useToast();

export default {
  components: { DetailBackButton, RqSummaryCard, RqSubmitModal },
  data() {
    return {
      order: null,
      myUserId: JSON.parse(localStorage.getItem("user") || "{}")?.user?.user_id,
    };
  },
  computed: {
    ...mapGetters(["sideMenuData"]),
    orderId() {
      return this.$route.params.id;
    },
    detailPath() {
      return `/replacement-quote/detail/${this.orderId}`;
    },
    // Admin Utilities → Edit Summary (?from=admin): admins may edit any order's summary
    fromAdmin() {
      return this.$route.query.from === "admin" && Array.isArray(this.sideMenuData) && menuHasRoute(this.sideMenuData, "/admin-utilities");
    },
    crumbs() {
      if (this.fromAdmin) return [{ label: "Admin Utilities", to: "/admin-utilities" }, { label: "System Summary" }];
      return [
        ...(isSilentLogin() ? [] : [{ label: "Replacement Quote History", to: "/work-order/replacement-quote" }]),
        { label: "Order Details", to: this.detailPath },
        { label: "System Summary" },
      ];
    },
    // opened by Submit PerfectMatch on the RQ detail
    isSubmitFlow() {
      return String(this.$route.query.submit || "") === "1" && this.canEdit;
    },
    // same rule as the RQ detail's old Edit Summary button: the tech on the order, not a helper
    canEdit() {
      if (this.fromAdmin) return true;
      const o = this.order || {};
      const me = String(this.myUserId);
      return [o.technician?.user_id, o.seller_id, o.installer_id].map(String).includes(me) && String(o.helper_id) !== me;
    },
  },
  watch: {
    orderId: { immediate: true, handler() { this.load(); } },
  },
  methods: {
    // the order only decides Edit Summary; the card loads the summary itself
    async load() {
      const res = await rqApi.detail(this.orderId);
      this.order = res.ok ? res.body : null;
    },
    onFailed(message) {
      toast.error(message);
      this.$router.replace(this.detailPath);
    },
    openCrumb(event, to) {
      if (isNewTabClick(event)) return;
      event.preventDefault();
      goBackTo(this.$router, to);
    },
  },
};
</script>
